import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

const TaskForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const getDefaultDueDate = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  const [formData, setFormData] = useState({
    taskTitle: '',
    description: '',
    taskType: 'Follow-up',
    priority: 'Medium',
    status: 'Pending',
    dueDate: getDefaultDueDate(),
    leadId: '',
    dealId: '',
    assignedTo: '',
    createReminder: true
  });

  useEffect(() => {
    fetchLeads();
    if (id) fetchTask();
  }, [id]);

  const fetchLeads = async () => {
    try {
      const { data } = await axios.get('/api/crm/leads');
      setLeads(data.leads || []);
    } catch (error) {
    }
  };



  const fetchTask = async () => {
    try {
      const { data } = await axios.get(`/api/crm/tasks/${id}`);
      setFormData({
        taskTitle: data.taskTitle,
        description: data.description || '',
        taskType: data.taskType || 'Follow-up',
        priority: data.priority || 'Medium',
        status: data.status || 'Pending',
        dueDate: data.dueDate ? new Date(data.dueDate).toISOString().slice(0, 16) : '',
        leadId: data.leadId || '',
        dealId: data.dealId || '',
        assignedTo: data.assignedTo || '',
        createReminder: false
      });
    } catch (error) {
      toast.error('Failed to fetch task');
    }
  };

  const _hasValidAssignee = formData.assignedTo && formData.assignedTo !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = { ...formData };
      if (submitData.dueDate) {
        submitData.dueDate = new Date(submitData.dueDate).toISOString();
      } else {
        delete submitData.dueDate;
      }
      if (!submitData.leadId || submitData.leadId === '') {
        delete submitData.leadId;
      }
      if (!submitData.dealId || submitData.dealId === '') {
        delete submitData.dealId;
      }
      if (!submitData.assignedTo || submitData.assignedTo === '') {
        delete submitData.assignedTo;
      }
      
      if (id) {
        await axios.put(`/api/crm/tasks/${id}`, submitData);
        toast.success('Task updated');
      } else {
        await axios.post('/api/crm/tasks', submitData);
        toast.success('Task created');
      }
      navigate('/crm/tasks');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          type="button"
          onClick={() => navigate('/crm/tasks')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Tasks
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{id ? 'Edit Task' : 'New Task'}</h1>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Task Title *</label>
            <input
              type="text"
              required
              value={formData.taskTitle}
              onChange={(e) => setFormData({ ...formData, taskTitle: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
              placeholder="Enter task title"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows="4"
            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            placeholder="Add task description..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Task Type</label>
            <select
              value={formData.taskType}
              onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="Follow-up">Follow-up</option>
              <option value="Call">Call</option>
              <option value="Email">Email</option>
              <option value="Meeting">Meeting</option>
              <option value="Demo">Demo</option>
              <option value="Site Visit">Site Visit</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Due Date</label>
            <input
              type="datetime-local"
              value={formData.dueDate}
              min={new Date().toISOString().slice(0, 16)}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              onBlur={(e) => e.target.blur()}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Related Lead</label>
            <select
              value={formData.leadId}
              onChange={(e) => setFormData({ ...formData, leadId: e.target.value })}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:border-blue-500 focus:outline-none transition-colors"
            >
              <option value="">Select Lead</option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>{lead.name} {lead.company ? `(${lead.company})` : ''}</option>
              ))}
            </select>
          </div>
        </div>



        <div className="flex gap-4 pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                💾 {id ? 'Update Task' : 'Save Task'}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/crm/tasks')}
            className="bg-gray-100 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskForm;
